import React, { useRef, useCallback } from 'react';
import { useInput, useNotify, useTranslate } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box, Button, TextField, TextFieldProps, Typography } from '@mui/material';
import Encoding from "encoding-japanese";

// コンポーネントのプロパティの型定義
interface FileContentInputProps extends Omit<TextFieldProps, 'label' | 'helperText' | 'source' | 'validate'> {
  source: string;
  label?: string; // TextField標準のlabelではなく、外側に表示するラベル
  helperText?: string | React.ReactNode; // TextField標準のhelperTextではなく、外側に表示するテキスト
  validate?: any;
  accept?: string; // input type="file" の accept 属性
  buttonLabel?: string; // ファイル読み込みボタンのラベル
  inputLabel?: string; // TextInput (TextField) 自体のラベル (通常は空にする)
  inputHelperText?: string | false; // TextInput (TextField) 自体のヘルパーテキスト
}

export const FileContentInput: React.FC<FileContentInputProps> = ({
  source,
  label,
  helperText,
  validate,
  accept = '.txt,text/plain', // デフォルトの受け入れ形式
  buttonLabel = 'テキストファイル読込',
  inputLabel = '', // デフォルトは空
  inputHelperText = false, // デフォルトは非表示
  rows = 10, // デフォルトの行数
  ...rest // TextField に渡す残りのプロパティ (e.g., fullWidth)
}) => {
  const translate = useTranslate();
  const { setValue } = useFormContext(); // react-hook-form の setValue を取得
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notify = useNotify();

  // useInput フックを使って React Admin のフォーム状態と連携
  // label や helperText はここで指定せず、TextFieldに直接渡すか外側で表示する
  const {
    field,
    fieldState: { error, isTouched, invalid }, // バリデーションエラー情報を取得
    formState: { isSubmitted }
  } = useInput({ source, validate });

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }

      // input要素のaccept属性はブラウザ依存のため、追加でチェック
      const allowedTypes = accept.split(',').map((t) => t.trim());
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      const isAllowed = allowedTypes.some((type) => {
        if (type.startsWith('.')) {
          // 拡張子チェック
          return fileName.endsWith(type);
        }
        if (type.includes('/')) {
          // MIMEタイプチェック
          return fileType === type || (type.endsWith('/*') && fileType.startsWith(type.replace('/*', '')));
        }
        return false;
      });

      if (!isAllowed) {
        notify(
          translate('ra.validation.accept', { _: `許可されていないファイル形式です。許可されている形式: ${accept}` }),
          { type: 'warning' }
        );
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const buffer = e.target?.result as ArrayBuffer;
        if (!buffer) throw new Error('Failed to read file buffer.');

        const codes = new Uint8Array(buffer);
        const detectedEncoding = Encoding.detect(codes);
        const content = Encoding.convert(codes, {
          to: 'UNICODE',
          from: detectedEncoding || 'AUTO',
          type: 'string',
        }) as string;

        setValue(source, content, { shouldValidate: true, shouldDirty: true });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.onerror = (e) => {
        console.error('File reading error:', e);
        notify(translate('ra.message.file_read_error', { _: 'ファイルの読み込みに失敗しました。' }), { type: 'error' });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [accept, setValue, source, notify, translate]
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // コンポーネントのラベル (空の場合は source から生成)
  const translatedLabel = label || translate(`resources.settings.fields.${source}`, { _: source });
  // エラーメッセージ (useInputから取得)
  const errorMessage = !invalid
    ? null
    : typeof error?.message === 'string'
    ? translate(error.message, { _: error.message })
    : null;
  // ヘルパーテキスト (エラーがあればエラーメッセージ、なければ指定のヘルパーテキスト)
  const displayedHelperText = (isTouched || isSubmitted) && invalid ? errorMessage : helperText;

  return (
    <Box sx={{ width: '100%', maxWidth: '600px', mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" gutterBottom component="label" htmlFor={field.name} sx={{ mb: 0 }}>
          {translatedLabel}
        </Typography>
        <Button variant="contained" size="small" onClick={handleButtonClick}>
          {translate(buttonLabel, { _: buttonLabel })}
        </Button>
        {/* 非表示のファイルインプット */}
        <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept={accept} />
      </Box>

      {/* テキストエリア (MUI TextField を使用) */}
      <TextField
        {...field}
        id={field.name}
        label={inputLabel}
        multiline
        rows={rows}
        error={(isTouched || isSubmitted) && invalid} // エラー状態
        helperText={inputHelperText === false ? undefined : inputHelperText} // TextField 自体のヘルパーテキスト
        fullWidth
        {...rest}
      />
      {/* 外側のヘルパーテキストまたはエラーメッセージ */}
      {displayedHelperText && (
        <Typography
          variant="caption"
          sx={{
            color: (isTouched || isSubmitted) && invalid ? 'error.main' : 'text.secondary',
            marginTop: '4px', // TextFieldのヘルパーテキストと似たマージン
            display: 'block'
          }}
        >
          {displayedHelperText}
        </Typography>
      )}
    </Box>
  );
};

export default FileContentInput;
